import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useState } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Table } from "@pentabd/ui";

import {
  ErrorModal,
  OrderGroup,
  SearchBox,
  SelectControllerVertical,
} from "@components";
import { PromotionCandidatesModal } from "./PromotionCandidatesModal";
import { TableColumn } from "./TableColumn";

import { createPromotionGroup } from "@api/promotions";

import { usePromotionCandidates } from "@hooks/HRM/employees/usePromotionCandidates";
import { useBaseData } from "@hooks/useBaseData";
import { useLanguage } from "@hooks/useLanguage";

import { TablePagination } from "@helpers/tablePagination";
import {
  getConvertedPositionData,
  getConvertedUnitData,
  getConvertedOfficeAddress,
} from "@helpers/baseDataConvert";

import { ROUTES } from "@constants/routes";
import { STATUS } from "@constants/status.hrm";
import { BASE_DATA } from "@constants/base-data";

import {
  PromotionFormDataType,
  promotionFormValidation,
} from "@validations/HRM/promotion/promotionFormValidation";

const {
  HRM: { PROMOTION },
} = ROUTES;

const { SUCCESS, FAILED } = STATUS;

const { DESIGNATIONS, WINGS, OFFICE } = BASE_DATA;

export const PromotionCandidatesTable = () => {
  const [isErrorModalOpen, setIsErrorModalOpen] = useState<boolean>(false);
  const [isInitiateModalOpen, setIsInitiateModalOpen] =
    useState<boolean>(false);
  const [activeStatus, setActiveStatus] = useState<number | null>(null);
  const [selectedID, setSelectedID] = useState<(string | number)[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { language } = useLanguage();

  const { data: positions } = useBaseData(DESIGNATIONS);
  const { data: wings } = useBaseData(WINGS);
  const { data: office } = useBaseData(OFFICE);

  const navigate = useNavigate();
  const { t } = useTranslation();
  const { control, register, setValue } = useForm();

  const {
    employees,
    pagination,
    employeeRequest,
    handleQuery,
    handleOrder,
    queryParams,
  } = usePromotionCandidates();
  const { total } = pagination || {};

  const orderGroups = [
    {
      value: "gradeOrder",
      label: t("PROMOTION_CANDIDATES.TABLE.FILTER_BUTTONS.EMPLOYEE_GRADE"),
    },
    {
      value: "joiningDateOrder",
      label: t("PROMOTION_CANDIDATES.TABLE.FILTER_BUTTONS.JOINING_DATE"),
    },
    {
      value: "promotionOrder",
      label: t("PROMOTION_CANDIDATES.TABLE.FILTER_BUTTONS.PROMOTION_DATE"),
    },
  ];

  const status = orderGroups.map((item) => item.value);

  const ordGroupAction = (index: number) => {
    let statusId = 0;
    switch (index) {
      case 0:
        statusId = 0;
        break;
      case 1:
        statusId = 1;
        break;
      case 2:
        statusId = 2;
        break;
      default:
        statusId = 0;
        break;
    }

    setActiveStatus(statusId);
    handleOrder(status[statusId]);
  };

  const closeModal = () => {
    setIsInitiateModalOpen(false);
  };
  const closeErrorModal = () => setIsErrorModalOpen(false);

  const onSaveAction = () => {};

  const methods = useForm<PromotionFormDataType>({
    resolver: yupResolver(promotionFormValidation),
  });

  const onSubmit: SubmitHandler<PromotionFormDataType> = (
    data: PromotionFormDataType,
  ) => {
    setIsLoading(true);
    createPromotionGroup(
      {
        name: data.groupName,
        employeeId: selectedID,
      },
      ({ status, data }) => {
        setIsLoading(false);
        closeModal();
        if (status === SUCCESS) {
          navigate(`${PROMOTION}/${data.id}`);
        } else if (status === FAILED) {
          setIsErrorModalOpen(true);
        }
      },
    );
  };

  useEffect(() => {
    const reset = () => {
      setIsErrorModalOpen(false);
      setIsInitiateModalOpen(false);
    };
    return reset();
  }, []);

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        {isErrorModalOpen && (
          <ErrorModal
            isModalOpen={isErrorModalOpen}
            closeModal={closeErrorModal}
          />
        )}
        {isInitiateModalOpen && (
          <PromotionCandidatesModal
            isLoading={isLoading}
            isModalOpen={isInitiateModalOpen}
            onSaveAction={onSaveAction}
            closeModal={closeModal}
          />
        )}
        <Table
          loading={employeeRequest}
          header={{
            title: t("PROMOTION_CANDIDATES.TABLE.HEADER"),
            badge: {
              label: `${total || 0}`,
              size: "sm",
              type: "primary",
            },
            buttons: [
              <SearchBox
                key="search"
                control={control}
                handleQuery={handleQuery}
              />,
            ],
          }}
          columns={TableColumn(t)}
          rows={employees}
          headerExtension={{
            leftComponents: [
              <OrderGroup
                key="btn-group"
                orderGroupData={orderGroups}
                activeState={activeStatus}
                action={ordGroupAction}
                query={queryParams}
              />,
            ],
            rightComponents: [
              <div className="row promotion-candidate-list">
                <SelectControllerVertical
                  control={control}
                  label="PROMOTION_CANDIDATES.TABLE.FORM.OFFICE"
                  name="officeId"
                  options={getConvertedOfficeAddress(office, language)}
                  register={register}
                  setValue={setValue}
                  isSearchable
                  selectAction={(e) => {
                    handleQuery("officeId", e);
                  }}
                  portal
                  cols={4}
                  withoutLabel
                />

                <SelectControllerVertical
                  control={control}
                  label="PROMOTION_CANDIDATES.TABLE.FORM.DESIGNATION"
                  name="positionId"
                  options={getConvertedPositionData(positions, language)}
                  register={register}
                  setValue={setValue}
                  isSearchable
                  selectAction={(e) => {
                    handleQuery("positionId", e);
                  }}
                  portal
                  cols={4}
                  withoutLabel
                />

                <SelectControllerVertical
                  control={control}
                  label="PROMOTION_CANDIDATES.TABLE.FORM.UNIT"
                  name="unitId"
                  options={getConvertedUnitData(
                    wings.filter((item: any) => item?.name !== "ACC"),
                    language,
                  )}
                  register={register}
                  setValue={setValue}
                  isSearchable
                  selectAction={(e) => {
                    handleQuery("unitId", e);
                  }}
                  portal
                  cols={4}
                  withoutLabel
                />
              </div>,
            ],
          }}
          selectButtonLabel={t("PROMOTION_CANDIDATES.TABLE.INITIATE_BUTTON")}
          selectButtonProps={{
            type: "primary",
            size: "sm",
          }}
          check={{
            onClickDelete: (id: (string | number)[]) => {
              setSelectedID(id);
              setIsInitiateModalOpen(true);
            },
          }}
          pagination={TablePagination(pagination, handleQuery)}
        />
      </form>
    </FormProvider>
  );
};
